import { useEffect, useState } from 'react';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useUsersList } from 'hooks/api/use-users';
import { useInsight } from 'components/partials/insight-modal/insight-context';

export interface InsightUser {
  userId: string | number;
  hasAccess: boolean;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  [key: string]: unknown;
}

interface AssignedUser {
  userId: string | number;
  hasAccess: boolean;
}

interface UseUserAccessArgs {
  onUsersChange?: (assignedUsers: AssignedUser[]) => void;
}

interface UseUserAccessResult {
  loading: boolean;
  displayedData: InsightUser[];
  isAllUsersSelected: boolean;
  isIndeterminate: boolean;
  isToggleOn: boolean;
  onSelectAllUsers: (change: { hasAccess: boolean }) => void;
  onUserChange: (changedUser: InsightUser) => void;
  onToggleChange: (toggled: boolean) => void;
}

export const useUserAccess = ({
  onUsersChange,
}: UseUserAccessArgs): UseUserAccessResult => {
  const currentUser = useCurrentUser();
  const { state: insightState, dispatch } = useInsight();

  const clientId =
    (insightState.metadata as Record<string, unknown>)?.clientId ||
    currentUser?.data?.client?.id;

  const shouldLoadUsers =
    !!clientId && !(insightState.allUsers && insightState.allUsers.length);

  const { data: usersData, loading: usersLoading } = useUsersList({
    pageSize: 100,
    filters: { clientId: clientId as string | number },
    enabled: shouldLoadUsers,
  });

  const [loading, setLoading] = useState(false);
  const [displayedData, setDisplayedData] = useState<InsightUser[]>([]);
  const [isAllUsersSelected, setIsAllUsersSelected] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [isToggleOn, setIsToggleOn] = useState(
    insightState.autoAssignToUsers || false
  );

  useEffect(() => {
    if (
      shouldLoadUsers &&
      usersData.length > 0 &&
      !insightState.allUsers?.length
    ) {
      const usersWithAccess: InsightUser[] = usersData.map((user) => ({
        ...user,
        userId: (user as Record<string, unknown>).id as string | number,
        hasAccess: false,
      }));

      dispatch({ type: 'setAllUsers', payload: usersWithAccess });
    }
  }, [usersData, shouldLoadUsers]);

  useEffect(() => {
    setLoading(usersLoading);
  }, [usersLoading]);

  const notifyUsersChange = (users: InsightUser[]) => {
    const assignedUsers: AssignedUser[] = users
      .filter((user) => user.hasAccess)
      .map((user) => ({
        userId: user.userId,
        hasAccess: user.hasAccess,
      }));

    dispatch({ type: 'setAssignees', payload: assignedUsers });
    onUsersChange?.(assignedUsers);
  };

  const onSelectAllUsers = ({ hasAccess }: { hasAccess: boolean }) => {
    const updatedUsers: InsightUser[] = displayedData.map((item) => ({
      ...item,
      hasAccess,
    }));

    setDisplayedData(updatedUsers);
    setIsAllUsersSelected(hasAccess);
    setIsIndeterminate(false);
    dispatch({ type: 'setAllUsers', payload: updatedUsers });
    notifyUsersChange(updatedUsers);
  };

  const onUserChange = (changedUser: InsightUser) => {
    const updatedUsers: InsightUser[] = displayedData.map((item) =>
      String(changedUser.userId) === String(item.userId)
        ? { ...item, hasAccess: changedUser.hasAccess }
        : item
    );

    setDisplayedData(updatedUsers);

    const checkedCount = updatedUsers.filter((user) => user.hasAccess).length;
    const allChecked = checkedCount === updatedUsers.length;
    const noneChecked = checkedCount === 0;

    setIsAllUsersSelected(allChecked);
    setIsIndeterminate(!allChecked && !noneChecked);

    dispatch({ type: 'setAllUsers', payload: updatedUsers });
    notifyUsersChange(updatedUsers);
  };

  const onToggleChange = (toggled: boolean) => {
    setIsToggleOn(toggled);
    dispatch({ type: 'setAutoAssignToUsers', payload: toggled });
  };

  useEffect(() => {
    const allUsers = insightState.allUsers as InsightUser[] | undefined;

    if (allUsers && allUsers.length > 0) {
      const checkedCount = allUsers.filter((user) => user.hasAccess).length;
      const allChecked = checkedCount === allUsers.length;
      const noneChecked = checkedCount === 0;

      setDisplayedData(allUsers);
      setIsAllUsersSelected(allChecked);
      setIsIndeterminate(!allChecked && !noneChecked);
    }
  }, [clientId, insightState.allUsers]);

  useEffect(() => {
    const allUsers = insightState.allUsers as InsightUser[] | undefined;

    if (allUsers && allUsers.length > 0) {
      const checkedCount = allUsers.filter((user) => user.hasAccess).length;
      const allChecked = checkedCount === allUsers.length;
      const noneChecked = checkedCount === 0;

      setDisplayedData(allUsers);
      setIsAllUsersSelected(allChecked);
      setIsIndeterminate(!allChecked && !noneChecked);
    }
  }, [insightState.allUsers]);

  useEffect(() => {
    setIsToggleOn(insightState.autoAssignToUsers || false);
  }, [insightState.autoAssignToUsers]);

  return {
    loading,
    displayedData,
    isAllUsersSelected,
    isIndeterminate,
    isToggleOn,
    onSelectAllUsers,
    onUserChange,
    onToggleChange,
  };
};
